import { createContext, useContext, useEffect, useState, type ReactNode } from 'react'
import {
  signIn as authSignIn,
  signOut as authSignOut,
  getCurrentUser,
  onAuthStateChange,
  isAdmin as checkIsAdmin,
  isDoctor as checkIsDoctor,
  isBSohtakDoctor as checkIsBsohtakDoctor,
  hasBackOfficeAccess,
  type AuthUser,
} from '@/lib/auth'
import { clearAuthRouteCache, getCachedAuth, setCachedAuth } from '@/lib/auth-cache'

// Build a minimal AuthUser from the persisted cache so the layout (sidebar,
// dashboard) can render the correct role-aware UI immediately on a fresh tab,
// without waiting for the async getCurrentUser() round-trip. The full profile
// is loaded right after and replaces this stub.
function readCachedUser(): AuthUser | null {
  const { cachedUserId, cachedUserRole } = getCachedAuth()
  if (!cachedUserId || !cachedUserRole) return null
  return { id: cachedUserId, user_role: cachedUserRole } as AuthUser
}

interface AuthContextType {
  user: AuthUser | null
  isLoading: boolean
  isAuthenticated: boolean
  isAdmin: boolean
  isDoctor: boolean
  /** Médecin B-SohTak (reçoit des dossiers) — distinct du médecin d'annuaire. */
  isBsohtakDoctor: boolean
  signIn: (email: string, password: string) => Promise<void>
  signOut: () => Promise<void>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function AuthProvider({ children }: { children: ReactNode }) {
  // Seed from per-tab cache so the sidebar picks the right role immediately
  // after a hard reload — avoids the doctor-sidebar flash for admins.
  const [user, setUser] = useState<AuthUser | null>(() => readCachedUser())
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    let isMounted = true

    // Check existing session on mount.
    // IMPORTANT: Never call signOut() here. If getCurrentUser() returns null
    // (timeout, slow network), keep the cached stub the route guard already
    // accepted — clearing user would force the wrong sidebar/role and break
    // role-aware UI. Real sign-out comes from the SIGNED_OUT event below.
    getCurrentUser()
      .then((freshUser) => {
        if (!isMounted) return
        if (freshUser && hasBackOfficeAccess(freshUser.user_role)) {
          setUser(freshUser)
          setCachedAuth(freshUser.id, freshUser.user_role)
        }
        // else: leave the cached stub in place
      })
      .catch(() => {
        // Network/timeout — keep cached stub
      })
      .finally(() => {
        if (!isMounted) return
        setIsLoading(false)
      })

    // Subscribe to auth changes — only react to SIGNED_OUT and TOKEN_REFRESHED.
    // INITIAL_SESSION: handled by getCurrentUser() above.
    // SIGNED_IN: handled by signIn() method below — the listener's getCurrentUser()
    // can race and timeout, setting user=null AFTER signIn() already set the user.
    const { data } = onAuthStateChange((user, event) => {
      if (!isMounted) return
      if (event === 'INITIAL_SESSION') return
      if (event === 'SIGNED_IN') return
      if (event === 'PASSWORD_RECOVERY') return

      if (event === 'TOKEN_REFRESHED' && user && hasBackOfficeAccess(user.user_role)) {
        setUser(user)
        setCachedAuth(user.id, user.user_role)
      } else if (event === 'SIGNED_OUT') {
        setUser(null)
        clearAuthRouteCache()
      }
    })

    return () => {
      isMounted = false
      data.subscription.unsubscribe()
    }
  }, [])

  const signIn = async (email: string, password: string) => {
    // Don't touch isLoading here — the form has its own local loading state for the button.
    // Setting AuthProvider's isLoading=true would unmount the Toaster and swallow toast errors.
    try {
      // authSignIn now returns the full user with DB role — no second getCurrentUser() needed
      const user = await authSignIn(email, password)
      setUser(user)
      setCachedAuth(user.id, user.user_role)
    } catch (error) {
      setUser(null)
      throw error
    }
  }

  const signOut = async () => {
    setIsLoading(true)
    try {
      await authSignOut()
      setUser(null)
      clearAuthRouteCache()
    } catch (error) {
      console.error('Error signing out:', error)
      throw error
    } finally {
      setIsLoading(false)
    }
  }

  const value: AuthContextType = {
    user,
    isLoading,
    isAuthenticated: !!user,
    isAdmin: user ? checkIsAdmin(user.user_role) : false,
    isDoctor: user ? checkIsDoctor(user.user_role) : false,
    // Lu sur le rôle (cache par onglet compris) : juste dès le premier rendu.
    isBsohtakDoctor: user ? checkIsBsohtakDoctor(user.user_role) : false,
    signIn,
    signOut,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

/**
 * Hook to require admin access
 * Redirects to sign-in if not authenticated or not admin
 */
export function useRequireAdmin() {
  const { user, isLoading, isAdmin } = useAuth()

  return { user, isLoading, isAdmin }
}

/**
 * Hook to require authentication (admin or doctor)
 * Redirects to sign-in if not authenticated
 */
export function useRequireAuth() {
  const { user, isLoading } = useAuth()

  return { user, isLoading }
}
